import type { AddressBookVariable } from '@integrations/address-books';
import type { CrmContact } from '@integrations/crm-contacts';
import type { CrmDeal } from '@integrations/crm-deals';
import type { FormElement } from '@common/types/form-element.type';

import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { PopoverModule } from 'ngx-bootstrap/popover';
import { TranslateModule } from '@ngx-translate/core';

import { ECrmCommentPlace, EFormInputType, EVariablesTypes } from '@common/enums';
import * as typeGuards from '@common/type-guards/form-elements.type-guards';

@Component({
    selector: 'sp-variables-popover',
    templateUrl: './sp-variables-popover.component.html',
    styleUrls: ['./sp-variables-popover.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [PopoverModule, TranslateModule],
})
export class SpVariablesPopoverComponent {
    @Input() public element!: FormElement;
    @Input() public addressBookVariablesList!: AddressBookVariable[];
    @Input() public crmContactsVariablesList!: CrmContact[];
    @Input() public crmDealsVariablesList!: CrmDeal[];
    @Input() public createAddressBook: boolean = false;
    @Input() public isCrmDealsListDisabled: boolean = false;
    @Input() public hideEmptyDealVariableAlert: boolean = false;

    public readonly typeGuards = typeGuards;
    public readonly variableTypes = EVariablesTypes;
    public readonly formInputType = EFormInputType;

    public allVariablesUnfilled(element: FormElement) {
        if (typeGuards.isSystemElement(element)) {
            return false;
        }

        const vars: any = [element.crmContactVariable, element.crmDealVariable];

        if (this.createAddressBook) {
            vars.push(element.variable);
        }

        return vars.every((val: any) => !val);
    }

    public someVariableUnfilled(element: FormElement) {
        if (typeGuards.isSystemElement(element)) {
            return false;
        }

        if (this.hideEmptyDealVariableAlert) {
            return false;
        }

        let vars: any = [element.crmContactVariable, element.crmDealVariable];

        if (this.isCrmDealsListDisabled) {
            vars = [element.crmContactVariable];
        } else {
            vars = [element.crmContactVariable, element.crmDealVariable];
        }

        if (this.createAddressBook) {
            vars.push(element.variable);
        }

        if (element.type === EFormInputType.hidden) {
            vars.push(element.value);
        }

        return !this.allVariablesUnfilled(element) && vars.some((val: any) => !val);
    }

    public noneOfCrmVariablesSelected(element: FormElement) {
        if (typeGuards.isSystemElement(element)) {
            return false;
        }

        const vars: any = [element.crmContactVariable, element.crmDealVariable];

        return vars.every((val: any) => !val);
    }

    public getVariableName(id: string | number, type: EVariablesTypes): string {
        let variableName: string = '';
        switch (type) {
            case EVariablesTypes.crmContactAttribute:
                variableName =
                    this.crmContactsVariablesList.find((variable: CrmContact) => variable.id === id)?.name ||
                    id.toString();
                break;
            case EVariablesTypes.crmDealAttribute:
                variableName =
                    this.crmDealsVariablesList.find((variable: CrmContact) => variable.id === id)?.name ||
                    id.toString();
                break;
            case EVariablesTypes.emailAddressBook:
                variableName =
                    this.addressBookVariablesList.find((variable: AddressBookVariable) => variable.variable_name === id)
                        ?.variable_name || id.toString(); // show id(name) for system variables
                break;
            default:
                break;
        }

        return variableName;
    }

    public getCrmCommentPlaceHint(element: FormElement) {
        const place = element.crmCommentPlace;

        switch (place) {
            case ECrmCommentPlace.crmDealComment:
                return ECrmCommentPlace.crmDealComment + '_popover_hint';
            case ECrmCommentPlace.crmContactComment:
                return ECrmCommentPlace.crmContactComment + '_popover_hint';
            default:
                return ECrmCommentPlace.crmAllComment + '_popover_hint';
        }
    }
}
